// Constants
import { HttpHeaders } from "@ms/uno-constants/lib/local/HttpConstants";
import { HttpStatus } from "@ms/uno-constants/lib/local/HttpStatusConstants";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
/**
 * Ajax client decorator that limits concurrent requests and circuit-breaks on repeated 429 responses.
 *
 * Concurrency: Maintains a count of in-flight requests. When the limit is reached, new requests
 * queue in FIFO order until a slot frees up.
 *
 * Circuit breaker: After 429 (Too Many Requests) responses accumulate without an intervening success,
 * and the count reaches a configurable threshold, all outgoing requests are paused for the Retry-After
 * duration (or a default pause). Non-429 failures do not reset the counter — only a successful response
 * does. This prevents retry storms where individual request retries amplify throttling.
 */ export class ThrottleAjaxClient {
    async executeRequest(url, options) {
        // Wait until the request may start: acquire a concurrency slot and, if the circuit breaker is
        // open, wait for it to close. A single queue timeout spans both waits and rejects (leaving no
        // slot held) if it elapses first.
        await this.waitToStart();
        try {
            const response = await this.innerAjaxClient.executeRequest(url, options);
            this.onSuccess();
            return response;
        } catch (error) {
            this.onFailure(error);
            throw error;
        } finally{
            this.releaseSlot();
        }
    }
    /**
     * Waits until the request is allowed to start: first for a concurrency slot, then (if the circuit
     * breaker is open) for it to close. When a positive queueTimeoutMs is configured, a single timeout
     * spans BOTH waits — so a request never waits more than queueTimeoutMs total before starting,
     * whether it is blocked behind the concurrency limit, the open circuit breaker, or both.
     * On timeout it rejects with an IFailedRequestResult (the failure shape used across the ajax
     * clients) and leaves no slot held and no entry in the wait queue.
     */ async waitToStart() {
        const { queueTimeoutMs } = this.config;
        // No timeout configured: wait indefinitely for a slot, then for the circuit breaker.
        if (!(queueTimeoutMs > 0)) {
            await this.acquireSlot(null);
            if (this.circuitBreakerPromise != null) {
                await this.circuitBreakerPromise;
            }
            return;
        }
        // Single timer shared by both waits. Rejecting it surfaces a throttle-layer timeout.
        let timeoutHandle;
        const timeoutPromise = new Promise((_resolve, reject)=>{
            timeoutHandle = setTimeout(()=>{
                this.loggers.traceLogger.logTrace(0x1e04b5de /* tag_4blx4 */ , TraceLevel.Warning, `Request timed out waiting to start [QueueTimeoutMs=${queueTimeoutMs}][QueueDepth=${this.waitQueue.length}]`);
                reject(this.createQueueTimeoutResult(queueTimeoutMs));
            }, queueTimeoutMs);
        });
        try {
            // Phase 1: acquire a concurrency slot. acquireSlot removes its own queue entry on timeout.
            await this.acquireSlot(timeoutPromise);
            // Phase 2: a slot is held now. If the circuit breaker is open, wait for it to close under
            // the same timeout; release the held slot if the timeout wins.
            if (this.circuitBreakerPromise != null) {
                try {
                    await Promise.race([
                        this.circuitBreakerPromise,
                        timeoutPromise
                    ]);
                } catch (error) {
                    this.releaseSlot();
                    throw error;
                }
            }
        } finally{
            clearTimeout(timeoutHandle);
        }
    }
    /**
     * Builds the rejection payload used when a request times out waiting in the throttle layer.
     * Uses the IFailedRequestResult shape (response + error) so callers handle it consistently with
     * other ajax-client failures instead of receiving a bare Error.
     */ createQueueTimeoutResult(timeoutMs) {
        return {
            response: null,
            error: new Error(`ThrottleAjaxClient queue timeout after ${timeoutMs}ms waiting to start`)
        };
    }
    /**
     * Waits until a concurrency slot is available.
     * If under the limit, resolves immediately. Otherwise queues the request; when a shared
     * timeoutPromise is provided, races against it and removes this request's queue entry if the
     * timeout wins, so a freed slot is never handed to a request that already gave up.
     */ acquireSlot(timeoutPromise) {
        if (this.inFlightCount < this.config.maxConcurrentRequests) {
            this.inFlightCount++;
            return Promise.resolve();
        }
        const queued = {
            resolve: ()=>{}
        };
        const slotPromise = new Promise((resolve)=>{
            queued.resolve = resolve;
        });
        this.waitQueue.push(queued);
        this.loggers.traceLogger.logTrace(0x1e04b5df /* tag_4blx5 */ , TraceLevel.Warning, `Request queued — concurrency limit reached [InFlight=${this.inFlightCount}][QueueDepth=${this.waitQueue.length}]`);
        if (timeoutPromise == null) {
            return slotPromise;
        }
        return Promise.race([
            slotPromise,
            timeoutPromise
        ]).catch((error)=>{
            // Timed out while still queued — drop our entry so a freed slot isn't handed to us. (A
            // pending hand-off would have resolved slotPromise via a microtask before this macrotask
            // timeout fired, so reaching here means we were never granted a slot.)
            const index = this.waitQueue.indexOf(queued);
            if (index !== -1) {
                this.waitQueue.splice(index, 1);
            }
            throw error;
        });
    }
    /**
     * Releases a concurrency slot and processes the next queued request if any.
     */ releaseSlot() {
        const next = this.waitQueue.shift();
        if (next) {
            // Hand the slot directly to the next waiter (inFlightCount stays the same)
            next.resolve();
        } else {
            this.inFlightCount--;
        }
    }
    /**
     * Called on successful response. Resets the 429 counter.
     */ onSuccess() {
        if (this.count429SinceLastSuccess > 0) {
            this.count429SinceLastSuccess = 0;
        }
    }
    /**
     * Called on failed response. Tracks 429s since last success and triggers circuit breaker when threshold is reached.
     * Non-429 failures are ignored — only a successful response resets the counter.
     */ onFailure(failureResult) {
        const status = failureResult.response?.status;
        if (status === HttpStatus.TooManyRequests) {
            this.count429SinceLastSuccess++;
            if (this.count429SinceLastSuccess >= this.config.circuitBreakerThreshold && !this.circuitBreakerOpen) {
                this.openCircuitBreaker(failureResult.response);
            }
        } else {
        // Non-429 failure does not reset the counter — only success resets it
        }
    }
    /**
     * Opens the circuit breaker, pausing all new requests for the Retry-After duration.
     */ openCircuitBreaker(response) {
        this.circuitBreakerOpen = true;
        const retryAfterRaw = response?.headers.get(HttpHeaders.RetryAfter) ?? null;
        const pauseMs = this.getRetryAfterMs(response);
        this.loggers.traceLogger.logTrace(0x1e04b68b /* tag_4bl0l */ , TraceLevel.Warning, `Circuit breaker opened — pausing all requests [Count429SinceLastSuccess=${this.count429SinceLastSuccess}][RetryAfterHeader=${retryAfterRaw}][PauseMs=${pauseMs}]`);
        this.circuitBreakerPromise = new Promise((resolve)=>{
            setTimeout(()=>{
                this.circuitBreakerOpen = false;
                this.circuitBreakerPromise = null;
                this.count429SinceLastSuccess = 0;
                this.loggers.traceLogger.logTrace(0x1e04b68a /* tag_4bl0k */ , TraceLevel.Info, `Circuit breaker closed — resuming requests`);
                resolve();
            }, pauseMs);
        });
    }
    /**
     * Extracts the pause duration from the Retry-After header on a 429 response.
     * Falls back to the configured default if the header is absent or not parseable.
     * Clamped to maxCircuitBreakerPauseMs to prevent unreasonably long pauses.
     */ getRetryAfterMs(response) {
        const retryAfterValue = response?.headers.get(HttpHeaders.RetryAfter) ?? null;
        if (!retryAfterValue) {
            return this.config.circuitBreakerPauseMs;
        }
        const retryAfterSeconds = Number(retryAfterValue);
        if (!isNaN(retryAfterSeconds) && retryAfterSeconds > 0) {
            return Math.min(retryAfterSeconds * 1000, this.config.maxCircuitBreakerPauseMs);
        }
        return this.config.circuitBreakerPauseMs;
    }
    constructor(ajaxClient, config, loggers){
        /** Number of requests currently in-flight */ this.inFlightCount = 0;
        /** FIFO queue of requests waiting for a concurrency slot */ this.waitQueue = [];
        /** Count of 429 responses since the last success (non-429 failures do not reset this) */ this.count429SinceLastSuccess = 0;
        /** Whether the circuit breaker is currently open (pausing all requests) */ this.circuitBreakerOpen = false;
        /** Promise that resolves when the circuit breaker pause ends */ this.circuitBreakerPromise = null;
        this.innerAjaxClient = ajaxClient;
        this.config = config;
        this.loggers = loggers;
        loggers.traceLogger.logTrace(0x1e04b68c /* tag_4bl0m */ , TraceLevel.Verbose, `Initializing ThrottleAjaxClient [MaxConcurrent=${config.maxConcurrentRequests}][CircuitBreakerThreshold=${config.circuitBreakerThreshold}][CircuitBreakerPauseMs=${config.circuitBreakerPauseMs}]`);
    }
}
